import axios from 'axios';

import RequestMethod from 'core/axios/RequestMethod';
import { ensureArray, conditionalArray } from 'utils/arrays';
import { ensureObject } from 'utils/objects';

// Count of the requests that'll be blocked if surpassed by the same request[body,params,method]
const MAX_REQUEST_COUNT = 10;
// Time window of the requests being made (in milliseconds)
const TIME_WINDOW = 5000;
// Cached map of the requests, will be set by unique keys of each request.
const REQUEST_LOG = new Map();
// Interval time for cleanup
const CLEANUP_INTERVAL = TIME_WINDOW * 5;

/**
 * Function that generates a unique key by using [body,params,method,data] of the request.
 * @param {axios.Request} request
 * @returns
 */
const generateUniqueKey = request => {
  const { method, url, params, data } = ensureObject(request);
  const dataMethods = [
    RequestMethod.PUT,
    RequestMethod.PATCH,
    RequestMethod.POST,
  ];
  return [
    method,
    url,
    JSON.stringify(params),
    ...conditionalArray(dataMethods.includes(method?.toUpperCase()), [
      JSON.stringify(data),
    ]),
  ].join('&');
};

/** Function to filter timestamps within the allowed time frame.
 * @param {Array<number>} timestamps
 * @param {number} currentTime
 * @returns {Array<number>}
 */
const filterRecentTimestamps = (timestamps, currentTime) =>
  timestamps.filter(timestamp => currentTime - timestamp < TIME_WINDOW);

// Periodic cleanup function to clear outdated logs.
const startPeriodicCleanup = () => {
  setInterval(() => {
    const currentTime = Date.now();
    REQUEST_LOG.forEach((timestamps, key) => {
      const recentTimestamps = filterRecentTimestamps(timestamps, currentTime);
      if (recentTimestamps.length > 0) {
        REQUEST_LOG.set(key, recentTimestamps);
      } else {
        REQUEST_LOG.delete(key);
      }
    });
  }, CLEANUP_INTERVAL);
};

const addRateLimitInterceptor = client => {
  client.interceptors.request.use(
    request => {
      const key = generateUniqueKey(request);

      // Get the current timestamp
      const currentTime = Date.now();
      // Get the list of timestamps for this request
      const timestamps = ensureArray(REQUEST_LOG.get(key));

      // Filter out timestamps that's older than 3 seconds
      const recentTimestamps = filterRecentTimestamps(timestamps, currentTime);
      recentTimestamps.push(currentTime);

      // Update the log with filtered timestamps
      REQUEST_LOG.set(key, recentTimestamps);

      // If the request has been made 10 times in the last 3 seconds, cancel the request
      if (recentTimestamps.length > MAX_REQUEST_COUNT) {
        const source = axios.CancelToken.source();
        request.cancelToken = source.token;

        const message = `Request to ${request.url} has been made too many times in a short period.`;

        console.warn(message);
        source.cancel(message);
      }

      return request;
    },
    error => Promise.reject(error),
  );

  // Response interceptor to clean up the log after the request is completed
  client.interceptors.response.use(
    response => {
      const key = generateUniqueKey(response.config);
      if (REQUEST_LOG.has(key)) {
        const currentTime = Date.now();
        const timestamps = REQUEST_LOG.get(key);
        const recentTimestamps = timestamps.filter(
          timestamp => currentTime - timestamp < TIME_WINDOW,
        );
        REQUEST_LOG.set(key, recentTimestamps);
      }
      return response;
    },
    error => Promise.reject(error),
  );

  startPeriodicCleanup();
};

export default addRateLimitInterceptor;
